import React, { useState, useEffect } from 'react';
import { Box, Typography, Tooltip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from '../WebSocketContext';
import { useSnackbar } from '../../Utils/SnackBarContext';
import FolderZipIcon from '@mui/icons-material/FolderZip';

const SettingDownloadFiles = ({ caseid }) => {
    const { t } = useTranslation();
    const { sendMessage, subscribeToMessages } = useWebSocket();
    const [isExporting, setIsExporting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const { openSnackbar } = useSnackbar();

    useEffect(() => {
        const unsubscribe = subscribeToMessages((parsedMessage) => {
            try {
                if (parsedMessage.command === 'downloadAllProjectUploadFiles') {
                    setIsExporting(false);
                    if (parsedMessage.data.status === 'success') {
                        // Trigger download
                        const link = document.createElement('a');
                        link.href = `data:application/zip;base64,${parsedMessage.data.data}`;
                        link.download = parsedMessage.data.filename;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        openSnackbar(t('download_files.success', 'Files downloaded successfully'));
                    } else {
                        console.error(parsedMessage.data.message);
                        openSnackbar(parsedMessage.data.message || t('download_files.error', 'Error downloading files'), 'error');
                    }
                }
            } catch (error) {
                console.error('Error parsing WebSocket message:', error);
                setIsExporting(false);
            }
        });

        return unsubscribe;
    }, [subscribeToMessages, openSnackbar, t]);

    const handleExport = () => {
        if (isExporting) return;
        setConfirmOpen(true);
    };

    const handleConfirmExport = () => {
        setConfirmOpen(false);
        setIsExporting(true);
        sendMessage({ command: 'downloadAllProjectUploadFiles', caseid });
    };

    const handleCancelExport = () => {
        setConfirmOpen(false);
    };

    return (
        <Box p={1} width={150}>
            <Box display="flex" flexDirection="column" alignItems="center">
                <Tooltip title={t("settings.download_all_files", "Download All Files")}>
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'inline-block',
                            cursor: isExporting ? 'default' : 'pointer',
                            opacity: isExporting ? 0.6 : 1,
                            transition: 'transform 0.2s',
                            '&:hover': {
                                transform: isExporting ? 'none' : 'scale(1.1)',
                            }
                        }}
                        onClick={handleExport}
                    >
                        <FolderZipIcon sx={{ fontSize: 48, color: '#5757b0' }} />
                        {isExporting && (
                            <CircularProgress
                                size={48}
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    color: '#5757b0'
                                }}
                            />
                        )}
                    </Box>
                </Tooltip>
                <Typography variant="caption">{t("settings.download_files_label", "Files")}</Typography>
            </Box>

            <Dialog open={confirmOpen} onClose={handleCancelExport}>
                <DialogTitle>{t('download_files.confirm_title', 'Download All Files')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('download_files.confirm_message', 'Are you sure you want to download all uploaded files for this project as a zip archive?')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelExport} color="secondary">
                        {t('cancel')}
                    </Button>
                    <Button onClick={handleConfirmExport} color="primary" variant="contained">
                        {t('download', 'Download')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SettingDownloadFiles;
